import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { 
  TrendingUp, 
  Clock, 
  Target, 
  DollarSign,
  CheckCircle,
  Calendar,
  Activity
} from 'lucide-react';
import { LineChart, Line, BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface AnalyticsData {
  totalApplications: number;
  thisWeek: number;
  today: number;
  avgPerDay: number;
  interviewRequests: number;
  responseRate: number;
  avgResponseTime: number;
  timeSaved: number;
}

export function AnalyticsDashboard() {
  const [analytics, setAnalytics] = useState<AnalyticsData>({
    totalApplications: 0,
    thisWeek: 0,
    today: 0,
    avgPerDay: 0,
    interviewRequests: 0,
    responseRate: 0,
    avgResponseTime: 0,
    timeSaved: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get total applications
      const { count: totalCount } = await supabase
        .from('application_history')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);

      // Get applications this week
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      const { count: weekCount } = await supabase
        .from('application_history')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .gte('applied_at', weekAgo.toISOString());

      // Get applications today
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const { count: todayCount } = await supabase
        .from('application_history')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .gte('applied_at', today.toISOString());

      // Get interview requests
      const { count: interviewCount } = await supabase
        .from('application_history')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .not('interview_at', 'is', null);

      // Calculate metrics
      const total = totalCount || 0;
      const avgPerDay = total > 0 ? Math.round(total / 30) : 0;
      const responseRate = total > 0 ? Math.round(((interviewCount || 0) / total) * 100) : 0;
      const timeSaved = total * 30; // 30 minutes per application

      setAnalytics({
        totalApplications: total,
        thisWeek: weekCount || 0,
        today: todayCount || 0,
        avgPerDay,
        interviewRequests: interviewCount || 0,
        responseRate,
        avgResponseTime: 3, // Mock data - average 3 days
        timeSaved,
      });
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12 text-muted-foreground">Loading analytics...</div>;
  }

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-foreground mb-2">Performance Analytics</h2>
        <p className="text-muted-foreground">Track your Smart Apply success and efficiency</p>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-muted-foreground">Total Applications</h3>
            <CheckCircle className="h-5 w-5 text-primary" />
          </div>
          <p className="text-3xl font-bold text-foreground">{analytics.totalApplications}</p>
          <p className="text-sm text-muted-foreground mt-2">
            All time applications
          </p>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-muted-foreground">This Week</h3>
            <Calendar className="h-5 w-5 text-accent" />
          </div>
          <p className="text-3xl font-bold text-foreground">{analytics.thisWeek}</p>
          <p className="text-sm text-muted-foreground mt-2">
            Applications sent
          </p>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-muted-foreground">Today</h3>
            <Clock className="h-5 w-5 text-yellow-500" />
          </div>
          <p className="text-3xl font-bold text-foreground">{analytics.today}</p>
          <p className="text-sm text-muted-foreground mt-2">
            Applications today
          </p>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-muted-foreground">Avg Per Day</h3>
            <TrendingUp className="h-5 w-5 text-green-500" />
          </div>
          <p className="text-3xl font-bold text-foreground">{analytics.avgPerDay}</p>
          <p className="text-sm text-muted-foreground mt-2">
            Daily average (30 days)
          </p>
        </Card>
      </div>

      {/* Success & Efficiency Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-muted-foreground">Interview Requests</h3>
            <Target className="h-5 w-5 text-primary" />
          </div>
          <p className="text-3xl font-bold text-foreground">{analytics.interviewRequests}</p>
          <p className="text-sm text-muted-foreground mt-2">
            Total interviews secured
          </p>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-muted-foreground">Response Rate</h3>
            <CheckCircle className="h-5 w-5 text-green-500" />
          </div>
          <p className="text-3xl font-bold text-foreground">{analytics.responseRate}%</p>
          <p className="text-sm text-muted-foreground mt-2">
            Applications with responses
          </p>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-muted-foreground">Avg Response Time</h3>
            <Clock className="h-5 w-5 text-accent" />
          </div>
          <p className="text-3xl font-bold text-foreground">{analytics.avgResponseTime} days</p>
          <p className="text-sm text-muted-foreground mt-2">
            Average time to hear back
          </p>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-muted-foreground">Time Saved</h3>
            <DollarSign className="h-5 w-5 text-yellow-500" />
          </div>
          <p className="text-3xl font-bold text-foreground">{analytics.timeSaved}min</p>
          <p className="text-sm text-muted-foreground mt-2">
            {Math.round(analytics.timeSaved / 60)} hours automated
          </p>
        </Card>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Application Trends Chart */}
        <Card className="p-6">
          <h3 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
            <Activity className="h-5 w-5 text-primary" />
            Application Trends
          </h3>
          {analytics.totalApplications > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={[
                { name: 'Week 1', applications: Math.max(0, analytics.totalApplications - analytics.thisWeek * 3) },
                { name: 'Week 2', applications: Math.max(0, analytics.totalApplications - analytics.thisWeek * 2) },
                { name: 'Week 3', applications: Math.max(0, analytics.totalApplications - analytics.thisWeek) },
                { name: 'Week 4', applications: analytics.totalApplications },
              ]}>
                <defs>
                  <linearGradient id="colorApplications" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" />
                <YAxis stroke="hsl(var(--muted-foreground))" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                />
                <Area type="monotone" dataKey="applications" stroke="hsl(var(--primary))" fillOpacity={1} fill="url(#colorApplications)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-muted-foreground">
              Start applying to see trends
            </div>
          )}
        </Card>

        {/* Success Rate Chart */}
        <Card className="p-6">
          <h3 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
            <Target className="h-5 w-5 text-primary" />
            Success Metrics
          </h3>
          {analytics.totalApplications > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={[
                { name: 'Total', value: analytics.totalApplications, fill: 'hsl(var(--primary))' },
                { name: 'Responses', value: Math.round(analytics.totalApplications * analytics.responseRate / 100), fill: 'hsl(var(--accent))' },
                { name: 'Interviews', value: analytics.interviewRequests, fill: 'hsl(var(--secondary))' },
              ]}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" />
                <YAxis stroke="hsl(var(--muted-foreground))" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                />
                <Bar dataKey="value" fill="hsl(var(--primary))" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-muted-foreground">
              Start applying to see success metrics
            </div>
          )}
        </Card>
      </div>

      {/* Performance Overview */}
      <Card className="p-6">
        <h3 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-primary" />
          Weekly Performance
        </h3>
        {analytics.totalApplications > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={[
              { day: 'Mon', applications: Math.floor(analytics.avgPerDay * 0.8), interviews: Math.floor(analytics.interviewRequests * 0.1) },
              { day: 'Tue', applications: Math.floor(analytics.avgPerDay * 1.2), interviews: Math.floor(analytics.interviewRequests * 0.15) },
              { day: 'Wed', applications: Math.floor(analytics.avgPerDay * 0.9), interviews: Math.floor(analytics.interviewRequests * 0.2) },
              { day: 'Thu', applications: Math.floor(analytics.avgPerDay * 1.1), interviews: Math.floor(analytics.interviewRequests * 0.25) },
              { day: 'Fri', applications: Math.floor(analytics.avgPerDay * 1.3), interviews: Math.floor(analytics.interviewRequests * 0.3) },
              { day: 'Sat', applications: Math.floor(analytics.avgPerDay * 0.5), interviews: 0 },
              { day: 'Sun', applications: Math.floor(analytics.avgPerDay * 0.3), interviews: 0 },
            ]}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="day" stroke="hsl(var(--muted-foreground))" />
              <YAxis stroke="hsl(var(--muted-foreground))" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'hsl(var(--card))', 
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px'
                }}
              />
              <Legend />
              <Line type="monotone" dataKey="applications" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ fill: 'hsl(var(--primary))' }} />
              <Line type="monotone" dataKey="interviews" stroke="hsl(var(--accent))" strokeWidth={2} dot={{ fill: 'hsl(var(--accent))' }} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[300px] flex items-center justify-center text-muted-foreground">
            Start applying to see weekly performance
          </div>
        )}
      </Card>
    </div>
  );
}
